// src/components/SelectFlight.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import LuggageModal from './LuggageModal';
import iconAller from '../../../public/assets/images/icon-aller.png';
import iconRetour from '../../../public/assets/images/icon-retour.png';

const selectFlightLayout = {
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 160px)' /* ajuste selon ton header */,
};

const flightFilters = {
  flexShrink: '0',
  marginBottom: '12px',
};

const flightResults = {
  flex: '1',
  overflowY: 'auto',
  paddingRight: '8px',
};

/* ===================== Config libellés ===================== */
const LABELS = {
  lang: 'fr', // 'fr' ou 'en'
  fr: {
    outbound: 'Aller',
    inbound: 'Retour',
    from: 'Départ',
    arrival: 'Arrivée',
    route: 'Route',
    flight: 'Vol',
    company: 'Compagnie',
    number: 'N°',
    baggages: 'Bagages',
    included: 'Inclus',
    none: '— Aucun supplément —',
    baggageOption: 'Bagages en Option',
    baggageHint:
      'Une seule option peut être choisie. Le total s’ajoute si le prix unitaire est fourni.',
  },
  en: {
    outbound: 'Outbound',
    inbound: 'Inbound',
    from: 'From',
    arrival: 'Arrival',
    route: 'Route',
    flight: 'Flight',
    company: 'Company',
    number: 'N°',
    baggages: 'Baggages',
    included: 'Included',
    none: '— No extra —',
    baggageOption: 'option Baggage ',
    baggageHint:
      'Only one option can be chosen. Total is added if the unit price is provided.',
  },
};
const T = (k) => LABELS[LABELS.lang][k];

/* ===================== Format helpers ===================== */
const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const fmtDateLong = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(LABELS.lang === 'fr' ? 'fr-FR' : 'en-GB', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    : '—';
const fmtDateShort = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(LABELS.lang === 'fr' ? 'fr-FR' : 'en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';
const fmtTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const eur = (n, c = 'EUR') =>
  n == null
    ? '—'
    : new Intl.NumberFormat(LABELS.lang === 'fr' ? 'fr-FR' : 'en-GB', {
        style: 'currency',
        currency: c,
      }).format(n);

/* ===================== Icônes ===================== */

const PlaneIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
    <path d="M2 19h20v2H2v-2zm8.5-7.1l1.6.5 8.4-5.2 1.5.5-6.1 7.2 4.1 1.3-.4 1.4-5.3-1.7-3.1 3.7-1.4-.4 1.5-4.6-4.8-1.5.5-1.4 4.5 1.4z" />
  </svg>
);

/* ===================== Métier ===================== */
const stripPrefix = (s, prefix = 'FLIGHTPLAN_') =>
  typeof s === 'string' && s.startsWith(prefix) ? s.slice(prefix.length) : s;

const getSupplement = (p, baseIncluded) => {
  const t = Number(p?.price?.total ?? 0);
  const inc = String(p?.apply || '').toLowerCase() === 'included';
  if (!Number.isFinite(t) || baseIncluded == null) return 0;
  return inc ? 0 : Number((t - Number(baseIncluded)).toFixed(2));
};

/* ---- Lecture robuste <Carrier> ---- */
function readNodeText(v) {
  if (v == null) return null;
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'object') {
    return (
      (v['#cdata'] && String(v['#cdata']).trim()) ||
      (v['#text'] && String(v['#text']).trim()) ||
      (v.cdata && String(v.cdata).trim()) ||
      (v.text && String(v.text).trim()) ||
      (v.value && String(v.value).trim()) ||
      null
    );
  }
  return null;
}
const IATA_NAME = {
  TU: 'Tunisair',
  TO: 'Transavia France',
  AF: 'Air France',
  VY: 'Vueling',
  U2: 'easyJet',
  LH: 'Lufthansa',
  ENT: 'ENT',
};
function getCarrier(leg = {}) {
  const carrierNode = leg.Carrier ?? leg.carrier ?? null;
  let name =
    readNodeText(carrierNode) ||
    leg.carrierName ||
    leg.airlineName ||
    leg.marketingCarrierName ||
    null;

  const code =
    (carrierNode &&
      carrierNode.Code &&
      (carrierNode.Code['@_Value'] || carrierNode.Code.Value)) ||
    leg.carrierCode ||
    leg.airlineCode ||
    (typeof leg.carrier === 'string' && leg.carrier.trim().length <= 3
      ? leg.carrier.trim()
      : null) ||
    null;

  if (name && /^[A-Z0-9]{1,3}$/.test(name)) name = null; // ne pas confondre code et nom
  if (!name && code && IATA_NAME[code.toUpperCase()]) name = IATA_NAME[code.toUpperCase()];
  return { name: name || null, code: code || null };
}

/* ---- Synthèse legs ---- */
function summarizeLegs(legs = []) {
  if (!Array.isArray(legs) || legs.length === 0) return null;
  const first = legs[0];
  const last = legs[legs.length - 1];
  const { name: carrierName, code: carrierCode } = getCarrier(first);
  return {
    from: first?.from || null,
    to: last?.to || null,
    begin: first?.begin || null,
    end: last?.end || null,
    carrier: carrierCode || null,
    carrierName: carrierName || null,
    flight: first?.flightNumber || null,
    directLabel: legs.length <= 1 ? 'Direct' : `${legs.length - 1} escale(s)`,
  };
}

/* ---- Bagages helpers ---- */
const toLuggageList = (lug) =>
  Array.isArray(lug) ? lug : [...(lug?.outbound || []), ...(lug?.inbound || [])];

function mergeLuggageOptions(luggages) {
  const list = toLuggageList(luggages);
  const map = new Map();
  for (const o of list) {
    const dir = String(o?.direction || '').includes('out') ? 'out' : 'in';
    const key = `${(o.code || '').toLowerCase()}|${(o.label || '').toLowerCase()}`;
    if (!map.has(key)) {
      map.set(key, { ...o, direction: dir });
    } else {
      const cur = map.get(key);
      cur.direction = 'both';
      if (cur.unitPrice == null && o.unitPrice != null) cur.unitPrice = o.unitPrice;
      if (cur.totalPrice == null && o.totalPrice != null) cur.totalPrice = o.totalPrice;
      map.set(key, cur);
    }
  }
  const out = [];
  for (const v of map.values()) {
    const dirLabel =
      LABELS.lang === 'fr'
        ? v.direction === 'both'
          ? 'aller + retour'
          : v.direction === 'out'
          ? 'aller'
          : 'retour'
        : v.direction === 'both'
        ? 'outbound + inbound'
        : v.direction === 'out'
        ? 'outbound'
        : 'inbound';
    const maxTxt = v?.quantity?.max ? ` · max ${v.quantity.max} / pax` : '';
    out.push({ value: v.code, payload: v, label: `${v.label} — ${dirLabel}${maxTxt}` });
  }
  out.sort((a, b) => a.label.localeCompare(b.label, LABELS.lang === 'fr' ? 'fr' : 'en'));
  return out;
}

function routeFromDescription(desc = '') {
  const s = String(desc || '');
  let m = s.match(/\b([A-Z]{3})\b[^A-Z0-9+/\\-]*[/\-][^A-Z0-9+/\\-]*\b([A-Z]{3})\b/);
  if (m) return { from: m[1], to: m[2] };
  m = s.match(
    /\b([A-ZÉÈÎÏÂÀÙÇ][A-ZÉÈÎÏÂÀÙÇ]+)\b[^/\\-]*[/\-][^/\\-]*\b([A-ZÉÈÎÏÂÀÙÇ][A-ZÉÈÎÏÂÀÙÇ]+)\b/i,
  );
  if (m) return { from: m[1], to: m[2] };
  return { from: null, to: null };
}

/* ===================== Logo compagnie ===================== */
function AirlineLogo({ code, name, urlBuilder }) {
  const [err, setErr] = useState(false);
  const upper = String(code || '').toUpperCase();
  const builder =
    urlBuilder || ((iata) => `https://pics.avs.io/80/40/${encodeURIComponent(iata)}.png`);
  if (!upper) return null;

  const defaultStyle = {
    height: 40,
    width: 'auto',
    objectFit: 'contain',
  };

  // 👉 Style spécial uniquement pour Transavia (TO)
  const transaviaStyle = {
    height: 50,
    width: 65, // ⇦ Mets la largeur que tu veux
    objectFit: 'contain',
  };

  const ENTStyle = {
    height: 20,
    width: 53,
    objectFit: 'contain',
  };

  if (!err) {
    return (
      <img
        src={builder(upper)}
        alt={name ? `Logo ${name}` : `Logo ${upper}`}
        style={upper === 'TO' ? transaviaStyle : upper === 'ENT' ? ENTStyle : defaultStyle}
        onError={() => setErr(true)}
      />
    );
  }
  return (
    <span
      className="badge text-bg-secondary"
      title={name || upper}
      style={{ letterSpacing: '0.5px' }}
    >
      {upper}
    </span>
  );
}

function airlineLogoUrlBuilder(iata) {
  // Si c'est Transavia (TO), on renvoie ton URL personnalisé
  const code = String(iata || '')
    .trim()
    .toUpperCase();
  if (code === 'TO') {
    return 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Transavia_logo.svg/1280px-Transavia_logo.svg.png';
  } else if (code === 'ENT') {
    return '/assets/images/ENT-logo.png';
  }
  // Sinon, comportement normal
  return `https://pics.avs.io/80/40/${encodeURIComponent(iata)}.png`;
}

/* ===================== Carte plan PGI ===================== */
function PgiCard({ plan, baseTotal = null, luggageUI, selected = false, onClick }) {
  if (!plan) return null;

  const price = plan.price || {};
  const total = Number(price.total || 0);
  const cur = price.currency || 'EUR';

  const ob = summarizeLegs(plan.outbound || []);
  const ib = summarizeLegs(plan.inbound || []);

  const desc = price.description || plan.description || '';
  const obGuess = !ob ? routeFromDescription(desc) : null;
  const ibGuess = !ib ? routeFromDescription(desc.split('/')[1] || desc) : null;

  const included =
    plan.apply === 'Included' || (baseTotal != null && Math.abs(total - baseTotal) < 0.01);
  const delta = baseTotal != null ? total - baseTotal : null;

  const displayId = stripPrefix(plan.id || plan.code || 'Vol');

  const obLeg0 = plan.outbound?.[0] || {};
  const ibLeg0 = plan.inbound?.[0] || {};

  const obCarrier = getCarrier(obLeg0); // { code, name }
  const ibCarrier = getCarrier(ibLeg0);
  // console.log(
  //   '%csrc\b2bcomponentsSelectFlight.jsx:299 ibCarrier',
  //   'color: #007acc;',
  //   ibCarrier,
  // );

  const obFlightNo = obLeg0?.flightNumber || ob?.flight || '—';
  const ibFlightNo = ibLeg0?.flightNumber || ib?.flight || '—';

  const mixedCarriers =
    obCarrier?.code &&
    ibCarrier?.code &&
    String(obCarrier.code).toUpperCase() !== String(ibCarrier.code).toUpperCase();

  const flightNoStyle = mixedCarriers ? { color: '#d32f2f' } : undefined; // rouge

  const mixedAirports =
    ob?.from && ib?.to && String(ob.from).toUpperCase() !== String(ib.to).toUpperCase();
  const airportDefaultStyle = { color: '#000064' }; // bleu bootstrap
  const routeDefaultStyle = { color: '#000064' };

  const warningNoteFR =
    "Attention : l'aéroport de départ à l'aller est différent de l'aéroport d'arrivée au retour. Vérifiez bien l'aéroport (temps de trajet, transferts et coûts supplémentaires).";

  const airportStyle = mixedAirports ? { color: '#d32f2f', fontWeight: 600 } : undefined;

  const Field = ({ label, value, sub }) => (
    <div className="d-flex flex-column align-items-start">
      <div className="text-muted small fw-semibold">{label}</div>
      <div className="fw-semibold">{value}</div>
      {sub ? <div className="small text-primary">{sub}</div> : null}
    </div>
  );

  const BaggageStatus = ({ included }) => (
    <span
      className="d-inline-flex align-items-center gap-1"
      title={included ? 'Bagage inclus' : 'Bagage non inclus'}
      style={{ cursor: 'help' }}
    >
      <span role="img" aria-label="bagage" style={{ fontSize: '21px' }}>
        🧳
      </span>

      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: 13,
          height: 13,
          borderRadius: '50%',
          backgroundColor: included ? '#2e7d32' : '#e53935', // vert / rouge
          margin: '5px 5px -10px -10px',
        }}
      />
    </span>
  );

  const baggageTitle = plan.baggageIncluded
    ? 'Bagage inclus'
    : plan.canBuyExtraBaggage
    ? 'Bagage en option'
    : 'Aucun bagage';

  const PriceBadge = () =>
    included ? (
      <span className="badge bg-success-subtle text-success border border-success-subtle">
        {T('included')}
      </span>
    ) : baseTotal != null ? (
      <span className="px-3 py-1 rounded text-white" style={{ background: '#0b1975' }}>
        +{eur(delta, cur)}
      </span>
    ) : (
      <span className="badge text-bg-secondary">{eur(total, cur)}</span>
    );

  const fmtTimeOr = (iso) => (iso ? fmtTime(iso) : null);
  const fmtDateOr = (iso) => (iso ? fmtDateShort(iso) : null);

  /* ----------- Icônes bagages (vert / rouge / noir) ----------- */
  const renderBaggageIcon = () => {
    // Bagage inclus
    if (plan.baggageIncluded) {
      return (
        <span className="d-inline-flex align-items-center gap-1 text-success">
          <i className="bi bi-suitcase-fill" />
          <i className="bi bi-check-circle-fill" />
        </span>
      );
    }

    // Pas inclus mais options dispo
    if (!plan.baggageIncluded && plan.canBuyExtraBaggage) {
      return (
        <span className="d-inline-flex align-items-center gap-1 text-dark">
          <i className="bi bi-suitcase-fill" />
          <i className="bi bi-three-dots" />
        </span>
      );
    }

    // Aucun bagage inclus, aucune option
    return (
      <span className="d-inline-flex align-items-center gap-1 text-danger">
        <i className="bi bi-suitcase-fill" />
        <i className="bi bi-x-circle-fill" />
      </span>
    );
  };

  return (
    <div
      className={`p-3 rounded border ${
        selected ? 'border-3 border-primary bg-light' : 'bg-white'
      } mb-5`}
      role="button"
      onClick={onClick}
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick?.()}
      style={{ outline: 'none', cursor: 'pointer' }}
    >
      {/* ====== OUTBOUND ====== */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          <span className="text-primary">
            <img src={iconAller} style={{ width: '40px', marginRight: '10px' }} />
          </span>
          <div className="fw-semibold">{T('outbound')}</div>
          {/* <div className="text-muted small ms-2">{displayId}</div> */}
        </div>
        <PriceBadge />
      </div>

      <div
        className="d-flex flex-wrap justify-content-between align-items-start gap-3"
        style={{ width: '100%' }}
      >
        <Field
          label={T('from')}
          value={fmtDateOr(ob?.begin)}
          sub={
            <>
              <span className="fw-semibold">{fmtTimeOr(ob?.begin) || '—:—'}</span>{' '}
              <span
                className="ms-1"
                style={airportStyle || { color: '#000064' }} // bleu par défaut
              >
                {ob?.from || obGuess?.from || '—'}
              </span>
            </>
          }
        />
        <div className="d-none d-md-flex align-items-center">
          {/* <PlaneIcon /> */}&#10132;
        </div>
        <Field
          label={T('arrival')}
          value={fmtDateOr(ob?.end)}
          sub={
            <>
              <span className="fw-semibold">{fmtTimeOr(ob?.end) || '—:—'}</span>{' '}
              <span className="text-primary ms-1">{ob?.to || obGuess?.to || '—'}</span>
            </>
          }
        />
        <Field
          label={T('route')}
          value={
            <span style={flightNoStyle || routeDefaultStyle}>
              {ob?.from || obGuess?.from || '—'} / {ob?.to || obGuess?.to || '—'}
            </span>
          }
          sub={ob?.directLabel || '—'}
        />
        <Field label={T('flight')} value="Direct" sub={ob?.directLabel || '—'} />
        <Field
          label={T('company')}
          value={
            <span className="d-flex align-items-center gap-2">
              <AirlineLogo
                code={obCarrier?.code}
                name={obCarrier?.name}
                urlBuilder={airlineLogoUrlBuilder}
              />
            </span>
          }
        />
        <Field label={T('number')} value={<span style={flightNoStyle}>{obFlightNo}</span>} />
        <Field label={T('baggages')} value="" />
        {/* 💼 Icônes bagage juste après le numéro */}
      </div>

      {/* ====== INBOUND ====== */}
      {(plan.inbound || []).length > 0 && (
        <>
          <div className="d-flex justify-content-between align-items-end">
            <hr className="my-3" style={{ width: '90%' }} />{' '}
            <div className="small mt-1">
              <BaggageStatus included={!!plan.baggageIncluded} title={baggageTitle} />
            </div>{' '}
          </div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center gap-2">
              <span className="text-primary">
                <img src={iconRetour} style={{ width: '40px', marginRight: '10px' }} />
              </span>
              <div className="fw-semibold">{T('inbound')}</div>
            </div>
          </div>
          <div
            className="d-flex flex-wrap justify-content-between align-items-start gap-3"
            style={{ width: '89%' }}
          >
            <Field
              label={T('from')}
              value={fmtDateOr(ib?.begin)}
              sub={
                <>
                  <span className="fw-semibold">{fmtTimeOr(ib?.begin) || '—:—'}</span>{' '}
                  <span className="text-primary ms-1">{ib?.from || ibGuess?.from || '—'}</span>
                </>
              }
            />
            <div className="d-none d-md-flex align-items-center">
              {/* <PlaneIcon /> */}&#10132;
            </div>
            <Field
              label={T('arrival')}
              value={fmtDateOr(ib?.end)}
              sub={
                <>
                  <span className="fw-semibold">{fmtTimeOr(ib?.end) || '—:—'}</span>{' '}
                  <span className="ms-1" style={airportStyle || { color: '#000064' }}>
                    {ib?.to || ibGuess?.to || '—'}
                  </span>
                </>
              }
            />
            <Field
              label={T('route')}
              value={
                <span style={flightNoStyle || routeDefaultStyle}>
                  {ib?.from || ibGuess?.from || '—'} / {ib?.to || ibGuess?.to || '—'}
                </span>
              }
              sub={ib?.directLabel || '—'}
            />
            <Field label={T('flight')} value="Direct" sub={ib?.directLabel || '—'} />
            <Field
              label={T('company')}
              value={
                <span className="d-flex align-items-center gap-2">
                  <AirlineLogo
                    code={ibCarrier?.code}
                    name={ibCarrier?.name}
                    urlBuilder={airlineLogoUrlBuilder}
                  />
                </span>
              }
            />
            <Field
              label={T('number')}
              value={<span style={flightNoStyle}>{ibFlightNo}</span>}
            />
          </div>
        </>
      )}

      {/* Option bagage (seulement sur la carte sélectionnée, inj. depuis le parent) */}
      {luggageUI && <div className="mt-3">{luggageUI}</div>}
    </div>
  );
}

/* ===================== Legacy card (si besoin) ===================== */
function LegacyCard({ f, direction, selected = false, onClick }) {
  if (!f) return null;
  return (
    <div
      className={`p-3 rounded border ${
        selected ? 'border-3 border-primary bg-white' : 'bg-light'
      } mb-5`}
      role="button"
      onClick={onClick}
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick?.()}
      style={{ outline: 'none', cursor: 'pointer' }}
    >
      <div className="text-muted small mb-1">
        <span className="text-uppercase">{f.airlineCode}</span> • {f.airlineName} •{' '}
        {T('flight')} {f.flightNumber}
      </div>
      <div className="row g-2">
        <div className="col-5">
          <div className="small text-muted">{fmtDateShort(f.takeoff)}</div>
          <div className="fw-semibold fs-6">
            {fmtTime(f.takeoff) || '—:—'}{' '}
            <span className="text-primary ms-2">{f.from?.code}</span>
          </div>
        </div>
        <div className="col-2 text-center text-primary">
          <PlaneIcon />
        </div>
        <div className="col-5 text-end">
          <div className="small text-muted">{fmtDateShort(f.landing)}</div>
          <div className="fw-semibold fs-6">
            {fmtTime(f.landing) || '—:—'}{' '}
            <span className="text-primary ms-2">{f.to?.code}</span>
          </div>
        </div>
      </div>
      <div className="small text-primary mt-1">
        {direction} – {f.from?.city} → {f.to?.city}
      </div>
    </div>
  );
}

/* ===================== Composant principal ===================== */
export default function SelectFlight({
  flights = [],
  outFlights = [],
  backFlights = [],
  origin,
  destination,
  startDate,
  endDate,
  adults = 0,
  children = 0,
  selectedPlanId, // 👈 pour restaurer le plan choisi
  initialLuggagesSelection,
  onSelect,
}) {
  const baseIncluded = useMemo(() => {
    const inc = (flights || []).find(
      (p) => (p.apply || '').toLowerCase() === 'included' || p.price?.included,
    );
    return Number(inc?.price?.includedBase ?? inc?.price?.total ?? 0);
  }, [flights]);

  // index du plan sélectionné dans flights
  const [selPlan, setSelPlan] = useState(0);
  const plan = flights[selPlan] || null;

  const [selOut, setSelOut] = useState(0);
  const [selBack, setSelBack] = useState(0);

  // bagages choisis par passager / par plan
  const [bagPerPaxByPlan, setBagPerPaxByPlan] = useState({}); // { [planId]: {A1:'CODE', C1:'CODE2', ...} }
  const [bagModalOpen, setBagModalOpen] = useState(false);
  const [airlineFilter, setAirlineFilter] = useState('ALL');

  const initRef = useRef(false);

  const thisPlanId = plan?.id || plan?.code || `PLAN_${selPlan}`;
  const bagPerPax = bagPerPaxByPlan[thisPlanId] || {};

  function computeBaggage(plan) {
    const included =
      !!plan?.baggageIncluded ||
      !!plan?.baggagesIncluded ||
      !!plan?.baggage?.included ||
      !!plan?.includedBaggage;
    console.log(
      '%csrc\b2bcomponentsSelectFlight.jsx:681 included',
      'color: #007acc;',
      included,
    );
    const option =
      !!plan?.canBuyExtraBaggage ||
      !!plan?.baggageOption ||
      !!plan?.baggage?.option ||
      (Array.isArray(plan?.luggages) && plan.luggages.length > 0); // fallback
    console.log('%csrc\b2bcomponentsSelectFlight.jsx:687 option', 'color: #007acc;', option);

    const status = included ? 'included' : option ? 'option' : 'none';
    return { status, included, option };
  }

  // 🔹 émet la sélection au parent (vol + bagages)
  const emitPlan = async (p, bagPayload = null) => {
    if (!onSelect || !p) return;
    console.log('%csrc\b2bcomponentsSelectFlight.jsx:678 p', 'color: #007acc;', p);
    const supplement = getSupplement(p, baseIncluded);
    const luggagesTotal = bagPayload?.total != null ? Number(bagPayload.total) : 0;
    const baggage = computeBaggage(p);
    return onSelect({
      planId: p?.id || null, // FLIGHTPLAN_19 (ID)
      planCode: p?.code || null, // gds_AMh...  ✅ (code produit)
      transportTotal: supplement, // tu peux garder si tu veux
      transportDelta: supplement, // ✅ IMPORTANT pour TripSelection
      luggagesTotal,
      transportBreakdown: {
        description: p?.price?.description || null,
        unit: Number(p?.price?.unit ?? 0) || null,
        qty: Number(p?.price?.qty ?? 0) || null,
        currency: p?.price?.currency || 'EUR',
      },

      baggage,
      luggagesSelection: bagPayload,
      plan: p || null,
    });
  };

  const emitLegacy = () => {
    if (!onSelect) return;
    onSelect({ out: outFlights[selOut] || null, back: backFlights[selBack] || null });
  };

  /* 🔁 1) Initialisation / restauration sélection vol
        - si selectedPlanId fourni → on essaie de le retrouver
        - sinon → plan "Included" ou index 0
        👉 S’exécute une seule fois par changement de flights / selectedPlanId,
           pas en boucle toutes les secondes. */
  useEffect(() => {
    if (initRef.current) return;
    if (!Array.isArray(flights) || flights.length === 0) return;

    let idx = 0;

    // si on a un plan mémorisé (retour de /paiement)
    if (selectedPlanId) {
      const found = flights.findIndex((p) => (p.id || p.code) === selectedPlanId);
      if (found >= 0) idx = found;
      else {
        const incIdx = flights.findIndex((p) => (p.apply || '').toLowerCase() === 'included');
        idx = incIdx >= 0 ? incIdx : 0;
      }
    } else {
      const incIdx = flights.findIndex((p) => (p.apply || '').toLowerCase() === 'included');
      idx = incIdx >= 0 ? incIdx : 0;
    }

    setSelPlan(idx);

    const chosenPlan = flights[idx];
    const planId = chosenPlan?.id || chosenPlan?.code || `PLAN_${idx}`;

    // 🔹 si on a déjà des bagages sauvegardés → on remplit la map
    let bagPayload = null;

    if (initialLuggagesSelection && initialLuggagesSelection.total > 0) {
      const perPax = initialLuggagesSelection.perPax || {};
      setBagPerPaxByPlan((m) => ({ ...m, [planId]: perPax }));
      bagPayload = initialLuggagesSelection;
    }

    emitPlan(chosenPlan, bagPayload);
    initRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flights]);

  /* ---- voyageurs pour la modale bagages ---- */
  const travellers = useMemo(() => {
    const A = Array.from({ length: Math.max(0, adults) }, (_, i) => ({
      id: `A${i + 1}`,
      type: 'Adult',
      label: `Adulte ${i + 1}`,
    }));
    const C = Array.from({ length: Math.max(0, children) }, (_, i) => ({
      id: `C${i + 1}`,
      type: 'Child',
      label: `Enfant ${i + 1}`,
    }));
    return [...A, ...C];
  }, [adults, children]);

  const noPlans = !Array.isArray(flights) || flights.length === 0;
  const noOut = !Array.isArray(outFlights) || outFlights.length === 0;
  const noBack = !Array.isArray(backFlights) || backFlights.length === 0;

  const mergedOptions = useMemo(
    () => (plan ? mergeLuggageOptions(plan?.luggages || []) : []),
    [plan],
  );

  const luggageOptionsForModal = useMemo(
    () =>
      mergedOptions.map((o) => {
        const p = o.payload || {};
        return {
          code: p.code || o.value,
          label: p.label || o.label,
          ppa: p.ppa ?? p.unitPrice ?? null,
          ppc: p.ppc ?? p.unitPrice ?? null,
          quantities: {
            adult: { min: 0, max: p.quantity?.max ?? 99 },
            child: { min: 0, max: p.quantity?.max ?? 99 },
          },
        };
      }),
    [mergedOptions],
  );

  // 1) Liste des compagnies disponibles (unique)
  const airlineOptions = useMemo(() => {
    const map = new Map(); // code -> { code, name, count }

    for (const p of flights || []) {
      const leg = p?.outbound?.[0] || p?.inbound?.[0] || null;
      if (!leg) continue;

      const { code, name } = getCarrier(leg); // déjà existant chez toi
      const c = (code || '').toUpperCase();
      if (!c) continue;

      const prev = map.get(c) || { code: c, name: name || c, count: 0 };
      prev.count += 1;
      map.set(c, prev);
    }

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [flights]);

  // 2) Index des vols à afficher selon le filtre
  const displayIndexes = useMemo(() => {
    const idxs = [];
    (flights || []).forEach((p, i) => {
      if (airlineFilter === 'ALL') {
        idxs.push(i);
        return;
      }
      const leg = p?.outbound?.[0] || p?.inbound?.[0] || null;
      const { code } = getCarrier(leg || {});
      if ((code || '').toUpperCase() === airlineFilter) idxs.push(i);
    });
    return idxs;
  }, [flights, airlineFilter]);

  // 3) Plans filtrés (ceux affichés)
  const displayFlights = useMemo(() => displayIndexes.map((i) => flights[i]).filter(Boolean), [
    displayIndexes,
    flights,
  ]);

  useEffect(() => {
    // si le plan sélectionné n’est plus dans la liste filtrée, on revient au 1er résultat
    if (!displayIndexes.includes(selPlan)) {
      setSelPlan(displayIndexes[0] ?? 0);
    }
  }, [airlineFilter, displayIndexes, selPlan]);

  // 🔹 total bagages = somme des unités selon bagPerPax
  const bagTotal = useMemo(
    () =>
      travellers.reduce((sum, t) => {
        const code = bagPerPax[t.id];
        if (!code) return sum;
        const opt = luggageOptionsForModal.find((x) => x.code === code);
        if (!opt) return sum;
        const unit = t.type === 'Child' ? opt.ppc ?? opt.ppa ?? 0 : opt.ppa ?? opt.ppc ?? 0;
        return sum + (unit || 0);
      }, 0),
    [bagPerPax, travellers, luggageOptionsForModal],
  );

  const hasPaidLuggage = useMemo(() => {
    if (!plan || !Array.isArray(plan.luggages)) return false;
    return plan.luggages.length > 0;
  }, [plan]);

  return (
    <div className="vstack gap-3" style={selectFlightLayout}>
      {noPlans && noOut && noBack && (
        <div className="alert alert-info mb-0 small">Chargement...</div>
      )}

      {!noPlans && (
        <div
          className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3"
          style={flightFilters}
        >
          <div
            className="d-flex fw-semibold justify-content-around"
            style={{ paddingBottom: '10px' }}
          >
            Filtrer par compagnie
          </div>
          <div className="d-flex mb-0 " style={{ paddingBottom: '10px' }}>
            {flights.length} Vols Disponibles
          </div>

          <div className="d-flex flex-wrap gap-2">
            {/* ✅ Toutes */}
            <button
              type="button"
              className={`btn btn-sm ${
                airlineFilter === 'ALL' ? 'btn-primary' : 'btn-outline-primary'
              }`}
              onClick={() => setAirlineFilter('ALL')}
            >
              Toutes <span className="ms-1 badge bg-light text-dark">{flights.length}</span>
            </button>

            {/* ✅ Compagnies */}
            {airlineOptions.map((a) => (
              // console.log('FILTER airline:', { code: a.code, name: a.name }),
              <button
                key={a.code}
                type="button"
                className={`btn btn-sm airline-btn ${
                  airlineFilter === a.code ? 'btn-primary text-dark' : 'btn-outline-primary'
                }`}
                onClick={() => setAirlineFilter(a.code)}
                title={a.name}
                style={{ padding: '0.346875rem .584375rem', marginLeft: '5px' }}
              >
                {/* Logo (si ton builder existe déjà) */}
                <span className="airline-logo-wrap">
                  <AirlineLogo
                    code={a.code}
                    name={a.name}
                    urlBuilder={airlineLogoUrlBuilder}
                  />
                </span>

                <span className={` ${airlineFilter === a.code ? 'text-white' : 'text-dark'}`}>
                  ({a.count})
                </span>
              </button>
            ))}
          </div>

          {/* <div className="text-muted small">
            {displayFlights.length} / {flights.length} vols
          </div> */}
        </div>
      )}

      {/* ---- LISTE PGI ---- */}
      {!noPlans && (
        <>
          <div className="vstack gap-2" style={flightResults}>
            {displayFlights.map((p, di) => {
              const realIndex = displayIndexes[di]; // index réel dans flights
              const selected = realIndex === selPlan;

              const mergedForCard =
                realIndex === selPlan
                  ? mergedOptions
                  : mergeLuggageOptions(p?.luggages || p?.luggageByDirection || []);
              return (
                <PgiCard
                  key={p.id || p.code || realIndex}
                  plan={p}
                  baseTotal={baseIncluded}
                  selected={selected}
                  onClick={async () => {
                    setSelPlan(realIndex);
                    const newPlanId = p.id || p.code || `PLAN_${realIndex}`;
                    const bagPayload = bagPerPaxByPlan[newPlanId] || null;
                    await emitPlan(p, bagPayload);
                  }}
                  luggageUI={
                    selected &&
                    plan.canBuyExtraBaggage &&
                    hasPaidLuggage &&
                    mergedForCard.length > 0 && (
                      <div className="mt-3 pt-3 border-top d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <span role="img" aria-label="baggage">
                            🧳
                          </span>
                          <div className="fw-semibold">{T('baggageOption')}</div>
                        </div>

                        <div className="d-flex align-items-center gap-3">
                          {bagTotal > 0 && (
                            <span className="badge text-bg-secondary">
                              +{eur(bagTotal, plan?.price?.currency || 'EUR')}
                            </span>
                          )}
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setBagModalOpen(true);
                            }}
                          >
                            Choisir les bagages
                          </button>
                        </div>
                      </div>
                    )
                  }
                  airlineLogoUrlBuilder={airlineLogoUrlBuilder}
                />
              );
            })}
          </div>
        </>
      )}

      {/* ---- Legacy listes ---- */}
      {noPlans && !noOut && (
        <>
          <div className="small text-primary">{`${T('outbound')} — ${fmtDateLong(
            startDate,
          )}`}</div>
          <div className="vstack gap-2">
            {outFlights.map((f, i) => (
              <LegacyCard
                key={f.id || i}
                f={f}
                direction={T('outbound')}
                selected={i === selOut}
                onClick={() => {
                  setSelOut(i);
                  emitLegacy();
                }}
              />
            ))}
          </div>
        </>
      )}

      {noPlans && !noBack && (
        <>
          <div className="small text-primary">{`${T('inbound')} — ${fmtDateLong(
            endDate,
          )}`}</div>
          <div className="vstack gap-2">
            {backFlights.map((f, i) => (
              <LegacyCard
                key={f.id || i}
                f={f}
                direction={T('inbound')}
                selected={i === selBack}
                onClick={() => {
                  setSelBack(i);
                  emitLegacy();
                }}
              />
            ))}
          </div>
        </>
      )}

      {/* ---- Modale bagages ---- */}
      <LuggageModal
        open={bagModalOpen}
        onClose={() => setBagModalOpen(false)}
        travellers={travellers}
        options={luggageOptionsForModal}
        defaultSelection={bagPerPax}
        currency={plan?.price?.currency || 'EUR'}
        onConfirm={(selMap, total) => {
          const planId = thisPlanId;
          setBagPerPaxByPlan((m) => ({ ...m, [planId]: selMap }));

          const detailedPerPax = {};
          for (const [travId, code] of Object.entries(selMap || {})) {
            if (!code) continue;

            const opt = luggageOptionsForModal.find((x) => x.code === code);
            if (!opt) continue;

            // prix selon type (adult/child)
            const isChild = String(travId).startsWith('C');
            const unit = isChild ? opt.ppc ?? opt.ppa ?? 0 : opt.ppa ?? opt.ppc ?? 0;

            // direction: si tu l’as dans payload (mergeLuggageOptions)
            // Sinon tu peux juste laisser null.
            detailedPerPax[travId] = {
              code: opt.code,
              label: opt.label,
              price: Number(unit || 0),
              direction: opt.direction || null, // optionnel si tu l’exposes
            };
          }

          const payload =
            total > 0
              ? {
                  perPax: detailedPerPax,
                  total,
                  currency: plan?.price?.currency || 'EUR',
                }
              : null;
          console.log('[BOOKING] payload keys:', Object.keys(payload || {}));
          console.log('[BOOKING] payload.planId:', payload?.planId);
          console.log('[BOOKING] payload.productCode:', payload?.productCode);
          console.log('[BOOKING] payload.planCode:', payload?.planCode);
          console.log('[BOOKING] payload.code:', payload?.code);
          console.log('[BOOKING] payload.baggage:', payload?.baggage);
          console.log(
            '[BOOKING] payload.selectedFlights?.baggage:',
            payload?.selectedFlights?.baggage,
          );
          emitPlan(plan, payload);
        }}
      />
    </div>
  );
}
